import { useCallback } from 'react';
import { compact } from 'lodash';

import { createInstructionsDataFromStructureInstructions } from 'src/components/RichSlate';
import { PROMPT_INSTRUCTION_TYPE } from 'src/data/generation';
import { useBrandVoicesActions } from 'src/store/brandVoices';

import { preparePromptFieldStructureInstructions } from './usePromptFieldStructureInstructions';

export const usePrepareStructureInstructionsDefaults = () => {
  const brandVoicesActions = useBrandVoicesActions();

  /**
   * Prepare initial instructions and value for "structure instructions" field
   * @param {Object} params
   * @param {GENERATION_TOOL} params.generationTool - The generation tool to use
   * @param {LANGUAGE} params.language - The language to use
   * @param {PROMPT_INSTRUCTION_TYPE[]} params.instructionTypes - The instruction types to use
   * @param {Array} [params.vocabularyIds] - The vocabulary IDs to use
   * @return {{ initialInstructions: PromptInstruction[], initialData: InstructionsData }} - The initial instructions and value
   */
  return useCallback(
    ({ generationTool, language, instructionTypes, vocabularyIds }) => {
      const brandVoicesWithTerms = brandVoicesActions.getVoicesWithTerms({ language });
      const customFormulas = brandVoicesActions.getDefaultVoiceCustomFormulas({ language });
      const customFormats = brandVoicesActions.getDefaultVoiceCustomFormats({ language });

      const availableInstructions = preparePromptFieldStructureInstructions({
        generationTool,
        brandVoicesWithTerms,
        customFormulas,
        customFormats,
        instructionTypes,
      });

      // Take "brand vocabularies" that are currently selected in this session
      const vocabularyInstructions = availableInstructions.filter((instr) => {
        if (instr.type !== PROMPT_INSTRUCTION_TYPE.vocabulary) {
          return false;
        }

        return vocabularyIds?.length > 0 ? vocabularyIds.includes(instr.id) : true;
      });

      // Take default "custom formula"
      const defaultFormula = getDefaultCustomFormula({
        customFormulas,
        generationTool,
      });
      const formulaInstruction = defaultFormula
        ? availableInstructions.find(
            (instr) =>
              instr.type === PROMPT_INSTRUCTION_TYPE.formula && instr.id === defaultFormula.id
          )
        : null;

      // Take default "custom format"
      const defaultFormats = getDefaultCustomFormats({
        customFormats,
        generationTool,
      });
      const formatInstructions = defaultFormats
        .map((defaultFormat) =>
          availableInstructions.find(
            (instr) =>
              instr.type === PROMPT_INSTRUCTION_TYPE.format && instr.id === defaultFormat.id
          )
        )
        .filter(Boolean);

      const initialInstructions = compact([
        ...vocabularyInstructions,
        formulaInstruction,
        ...formatInstructions,
      ]);
      const initialData = createInstructionsDataFromStructureInstructions(initialInstructions);

      return {
        initialInstructions,
        initialData,
      };
    },
    [brandVoicesActions]
  );
};

const getDefaultCustomFormula = ({ customFormulas, generationTool }) => {
  if (!customFormulas || customFormulas.length === 0) {
    return null;
  }

  return customFormulas.find(
    (formula) => formula.generationTool === generationTool && formula.isDefault
  );
};

const getDefaultCustomFormats = ({ customFormats, generationTool }) => {
  if (!customFormats || customFormats.length === 0) {
    return [];
  }

  return customFormats.filter((format) =>
    (format.defaultForGenerationTools || []).includes(generationTool)
  );
};
